/**
 * #1053 — may the caller attach files in this project's chat? The SERVER's answer (`GET /attachments/can-attach`,
 * the same `mayWriteWorkspace` the upload route refuses with), never a role (owner 2026-10-15, #872). So the composer
 * offers attaching exactly when the route would accept it: a Viewer granted Edit on the project can attach, a Member
 * set to Read-only cannot.
 *
 * Fail-closed: `false` until the answer for THIS workspace (and identity) arrives, and on a failed request. Tried and
 * reverted in review: "may" while loading let a view-only person put inline bytes in the composer that the SEND route
 * then refuses (the whole message, text included), and a request that never settles kept it that way. The answer is
 * asked the moment the project is known, so the closed window is one request long. A failure is retried once
 * a few seconds later, and the answer is asked again whenever the chat deck is shown again (it stays mounted while
 * hidden), so a grant changed meanwhile, or a blip, doesn't leave the composer wrong until a reload.
 */
import { useEffect, useState } from 'react'
import { useRefreshWhenShown } from '@papan/core-ui/ui'
import { fetchCanAttach } from './sessionAttachmentsApi'

const RETRY_AFTER_MS = 5_000

/** `identity` — the active org email the requests are made as; a different identity is asked again. */
export function useCanAttach(workspaceId: string | null | undefined, identity?: string | null): boolean {
  const [answer, setAnswer] = useState<{ key: string; canAttach: boolean } | null>(null)
  const [asks, setAsks] = useState(0)
  useRefreshWhenShown(() => setAsks((n) => n + 1))
  // And when the tab comes back into view — the deck signal above doesn't exist on mobile.
  useEffect(() => {
    const onVisible = () => { if (document.visibilityState === 'visible') setAsks((n) => n + 1) }
    document.addEventListener('visibilitychange', onVisible)
    return () => document.removeEventListener('visibilitychange', onVisible)
  }, [])
  useEffect(() => {
    if (!workspaceId) return
    const key = `${workspaceId}\u0000${identity ?? ''}`
    let live = true
    let retry: ReturnType<typeof setTimeout> | undefined
    const ask = (retryOnFailure: boolean) => {
      fetchCanAttach(workspaceId).then(
        (canAttach) => { if (live) setAnswer({ key, canAttach }) },
        () => {
          if (!live) return
          setAnswer({ key, canAttach: false })
          if (retryOnFailure) retry = setTimeout(() => { if (live) ask(false) }, RETRY_AFTER_MS)
        },
      )
    }
    ask(true)
    return () => { live = false; if (retry) clearTimeout(retry) }
  }, [workspaceId, identity, asks])
  if (!workspaceId) return false
  // An answer for a PREVIOUS workspace (or identity) never stands in for this one; none yet means "may not".
  return answer?.key === `${workspaceId}\u0000${identity ?? ''}` && answer.canAttach
}
